/**
 * Once a parent op (checkIn) has synced and the server returns its
 * calllogid, walk every dependent record that references the parent's
 * localId and stamp the calllogid onto it. Touches three stores:
 *
 *   - visit_sync_queue: stamp payload.callLogId on rows where
 *     dependsOn matches. Most child ops re-derive calllogid at sync
 *     time via resolveParentCallLogId, but the redundant stamp lets
 *     the Sync Issues UI show meaningful state without a join.
 *   - visit_survey_responses: stamp callLogId so the rep's "I
 *     submitted this survey" UI reflects the server-side row.
 *   - visit_file_queue: stamp callLogId on pending blob uploads so
 *     the file rows can be cross-referenced with the server CallLog.
 *
 * Idempotent: rows whose callLogId already equals the supplied value
 * are skipped so re-running this is safe.
 */

import { STORES, getAll, put } from '../utils/indexedDB';
import type { LocalId, QueueRow } from './types';

export async function reconcileChildren(
  parentLocalId: LocalId,
  callLogId: number,
): Promise<void> {
  const queueRows = (await getAll(STORES.VISIT_SYNC_QUEUE)) as QueueRow[];
  for (const row of queueRows) {
    if (row.dependsOn !== parentLocalId) continue;
    const payload = row.payload as { callLogId?: number | null } | null;
    if (!payload || typeof payload !== 'object') continue;
    if (payload.callLogId === callLogId) continue;
    await put(STORES.VISIT_SYNC_QUEUE, { ...row, payload: { ...payload, callLogId } });
  }

  const surveyRows = (await getAll(STORES.VISIT_SURVEY_RESPONSES)) as Array<{
    taskUUID?: string;
    callLogId?: number | null;
  }>;
  for (const row of surveyRows) {
    if (row.taskUUID !== parentLocalId) continue;
    if (row.callLogId === callLogId) continue;
    await put(STORES.VISIT_SURVEY_RESPONSES, { ...row, callLogId });
  }

  const fileRows = (await getAll(STORES.VISIT_FILE_QUEUE)) as Array<{
    taskUUID?: string;
    callLogId?: number | null;
  }>;
  for (const row of fileRows) {
    if (row.taskUUID !== parentLocalId) continue;
    if (row.callLogId === callLogId) continue;
    await put(STORES.VISIT_FILE_QUEUE, { ...row, callLogId });
  }
}
